import React, {
  useContext,
  useState,
  useEffect,
  FormEvent,
  KeyboardEvent,
} from "react";
import styled from "styled-components";
import * as Sentry from "@sentry/react";
import TextField from "../parts/TextField";
import { PaymentMethods, Features } from "../../../api/v1";
import { api } from "../../../api";
import Alert from "../parts/Alert";
import CheckboxField from "../parts/Checkbox";
import Select from "../parts/Select";
import Button from "../parts/Button";
import { SessionContext } from "../layouts/UserLayout";
import FilesField from "../parts/FilesField";
import ButtonSubmit from "../parts/ButtonSubmit";
import FormGroup from "../parts/FormGroup";
import { useNavigate } from "react-router-dom";
import TextAreaField from "../parts/TextAreaField";

const CreateShopFormStyle = styled(FormGroup)`
  background: none;
  text-align: center;
  max-width: 600px;
  margin: 0 auto 10px auto;
`;

const ImageUploadStyle = styled.div`
  display: flex;
  gap: 15px;
  text-align: left;
  flex-wrap: wrap;
  margin-bottom: 10px;
  width: 100%;
`;

const ImageStyle = styled.div`
  position: relative;
  display: inline-block;
  width: calc(100% / 3 - 15px);

  button {
    position: absolute;
    background: #393432;
    border: none;
    border-radius: 25px;
    color: white;
    cursor: pointer;
    font-size: 12px;
    width: 25px;
    height: 25px;
    top: -10px;
    right: -10px;
    padding: 0;
  }

  img {
    object-fit: cover;
    width: 100%;
    height: 130px;
  }

  @media (max-width: 767px) {
    width: calc(100% / 2 - 15px);
  }
`;

const PaymentMethodsGroupStyle = styled.div`
  position: relative;
  border: 1px solid rgb(219, 219, 219);
  border-radius: 25px;
  color: #383432;
  text-align: left;
  padding: 40px 20px 30px 20px;
  margin-bottom: 25px;

  .title {
    position: absolute;
    background: white;
    font-size: 16px;
    top: -15px;
    padding: 5px;
  }

  @media (max-width: 767px) {
    padding: 20px 10px 15px 10px;
  }
`;

const FeaturesGroupStyle = styled.div`
  position: relative;
  border: 1px solid rgb(219, 219, 219);
  border-radius: 25px;
  color: #383432;
  text-align: left;
  padding: 40px 20px 30px 20px;
  margin-bottom: 25px;

  .title {
    position: absolute;
    background: white;
    font-size: 16px;
    top: -15px;
    padding: 5px;
  }

  @media (max-width: 767px) {
    padding: 20px 10px 15px 10px;
  }
`;

const PaymentMethodCheckboxField = styled(CheckboxField)`
  display: inline-block;
  margin-right: 15px;
`;

const FeatureCheckboxField = styled(CheckboxField)`
  display: inline-block;
  margin-right: 15px;
`;

const BudgetGroupStyle = styled.div`
  position: relative;
  border: 1px solid rgb(219, 219, 219);
  border-radius: 25px;
  color: #383432;
  text-align: left;
  padding: 40px 20px 30px 20px;
  margin-bottom: 25px;

  .title {
    position: absolute;
    background: white;
    font-size: 16px;
    top: -15px;
    padding: 5px;
  }

  .timeframe-label {
    padding: 0 0 5px 5px;
  }

  .timeframe-budget {
    display: flex;
    gap: 10px;
    margin-bottom: 20px;
  }

  @media (max-width: 767px) {
    padding: 20px 10px 15px 10px;
  }
`;

const OpeningHoursGroupStyle = styled.div`
  position: relative;
  border: 1px solid rgb(219, 219, 219);
  border-radius: 25px;
  color: #383432;
  text-align: left;
  padding: 40px 20px 30px 20px;
  margin-bottom: 20px;

  .title {
    position: absolute;
    background: white;
    font-size: 16px;
    top: -15px;
    padding: 5px;
  }

  @media (max-width: 767px) {
    padding: 20px 10px 30px 10px;
  }
`;

const OpeningHoursFieldStyle = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
`;

const OpeningHoursAddButtonStyle = styled(Button)`
  flex-shrink: 0;
`;

const OpeningHoursTable = styled.table`
  border-collapse: collapse;
  margin-left: 5px;
  width: 100%;

  th {
    border-bottom: 1px solid rgb(219, 219, 219);
  }

  td {
    padding: 5px 0;
  }

  .deleteOpeningHour {
    p {
      color: red;
      cursor: pointer;
      margin: 0;
    }
  }
`;

const ButtonSubmitStyle = styled(ButtonSubmit)`
  max-width: 250px;
  width: 100%;
`;

const CreateDraftShopForm: React.FC = () => {
  const navigation = useNavigate();
  const dayOfWeeks = [
    { id: 0, value: "日曜日" },
    { id: 1, value: "月曜日" },
    { id: 2, value: "火曜日" },
    { id: 3, value: "水曜日" },
    { id: 4, value: "木曜日" },
    { id: 5, value: "金曜日" },
    { id: 6, value: "土曜日" },
    { id: 7, value: "祝日" },
  ];
  const dayOfWeekValues = dayOfWeeks.map((object) => object.value);
  const [paymentMethods, setPaymentMethods] = useState<Array<PaymentMethods>>(
    []
  );
  const [features, setFeatures] = useState<Array<Features>>([]);
  const [name, setName] = useState<string>("");
  const [nameKana, setNameKana] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [tel, setTel] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [homepage, setHomepage] = useState<string>("");
  const [googleMapUrl, setGoogleMapUrl] = useState<string>("");
  const [googleMapIframe, setGoogleMapIframe] = useState<string>("");
  const [nearestStation, setNearestStation] = useState<string>("");
  const [numberOfSeats, setNumberOfSeats] = useState<number>();
  const [paymentMethodIds, setPaymentMethodIds] = useState<Array<number>>([]);
  const [featureIds, setFeatureIds] = useState<Array<number>>([]);
  const [dayOfWeek, setDayOfWeek] = useState<number>();
  const [openingTime, setOpeningTime] = useState<string>("");
  const [closingTime, setClosingTime] = useState<string>("");
  const [morningLowerBudget, setMorningLowerBudget] = useState<number>(0);
  const [morningUpperBudget, setMorningUpperBudget] = useState<number>(0);
  const [lunchLowerBudget, setLunchLowerBudget] = useState<number>(0);
  const [lunchUpperBudget, setLunchUpperBudget] = useState<number>(0);
  const [eveningLowerBudget, setEveningLowerBudget] = useState<number>(0);
  const [eveningUpperBudget, setEveningUpperBudget] = useState<number>(0);
  const [openingHours, setOpeningHours] = useState<
    Array<{ dayOfWeek: number; openingTime: string; closingTime: string }>
  >([]);
  const [note, setNote] = useState<string>("");
  const [images, setImages] = useState<Array<{ id: number; url: string }>>([]);
  const [message, setMessage] = useState(null);
  const [messageLevel, setMessageLevel] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isEnterPressed, setIsEnterPressed] = useState<boolean>(false);
  const session = useContext(SessionContext);

  useEffect(() => {
    api
      .getPaymentMethods()
      .then((response) => {
        setPaymentMethods(response.data);
      })
      .catch((error) => {
        if (error.response?.status === 401) return;
        Sentry.captureException(error);
      });
    api
      .getFeatures()
      .then((response) => {
        setFeatures(response.data);
      })
      .catch((error) => {
        if (error.response?.status === 401) return;
        Sentry.captureException(error);
      });
  }, []);

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter") {
      setIsEnterPressed(true);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (isEnterPressed) {
      setIsEnterPressed(false);
      return;
    }
    setSubmitting(true);
    await api
      .createDraftShop({
        shop: {
          name,
          nameKana,
          description,
          tel,
          address,
          homepage,
          googleMapUrl,
          googleMapIframe,
          nearestStation,
          numberOfSeats: numberOfSeats <= 0 ? null : numberOfSeats,
          paymentMethodIds,
          featureIds,
          draftBudgetsAttributes: [
            {
              timeframe: 0,
              lower: morningLowerBudget === 0 ? null : morningLowerBudget,
              upper: morningUpperBudget === 0 ? null : morningUpperBudget,
            },
            {
              timeframe: 1,
              lower: lunchLowerBudget === 0 ? null : lunchLowerBudget,
              upper: lunchUpperBudget === 0 ? null : lunchUpperBudget,
            },
            {
              timeframe: 2,
              lower: eveningLowerBudget === 0 ? null : eveningLowerBudget,
              upper: eveningUpperBudget === 0 ? null : eveningUpperBudget,
            },
          ],
          draftOpeningHoursAttributes: openingHours,
          requestUserId: session.userId,
          images,
          note,
          shopId: null,
        },
      })
      .then((response) => {
        navigation("/user/request-shops", {
          state: {
            message: response.data.message,
            level: "success",
          },
        });
      })
      .catch((error) => {
        setMessage(error.response.data.message);
        setMessageLevel("error");
      });

    setIsEnterPressed(false);
    setSubmitting(false);
  };

  const onClickDeleteImageButton = (index) => {
    const uploadedFiles = [...images];
    uploadedFiles.splice(index, 1);
    setImages(uploadedFiles);
  };

  const onClickAddOpeningHours = () => {
    const regex = /[0-9]{1,2}:[0-9]{2}/g;
    if (!openingTime && !closingTime) return;
    if (!openingTime.match(regex) || !closingTime.match(regex)) return;

    const latestOpeningHours = openingHours.concat({
      dayOfWeek: dayOfWeek,
      openingTime: openingTime,
      closingTime: closingTime,
    });
    setOpeningHours(latestOpeningHours);
  };

  const onClickDeleteOpeningHour = (index: number) => {
    const latestOpeningHours = openingHours.filter(
      (_, openingHoursIndex) => openingHoursIndex !== index
    );
    setOpeningHours(latestOpeningHours);
  };

  const onChangeCheckedPaymentMethods = (id: number) => {
    if (paymentMethodIds.includes(id)) {
      const latestCheckedPaymentMethods = paymentMethodIds.filter(
        (paymentMethod) => paymentMethod !== id
      );
      setPaymentMethodIds(latestCheckedPaymentMethods);
    } else {
      const latestCheckedPaymentMethods = paymentMethodIds.concat(id);
      setPaymentMethodIds(latestCheckedPaymentMethods);
    }
  };

  const onChangeFeatures = (id: number) => {
    if (featureIds.includes(id)) {
      const latestCheckedFeatures = featureIds.filter(
        (feature) => feature !== id
      );
      setFeatureIds(latestCheckedFeatures);
    } else {
      const latestCheckedFeatures = featureIds.concat(id);
      setFeatureIds(latestCheckedFeatures);
    }
  };

  return (
    <CreateShopFormStyle onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      {images.length !== 0 && (
        <ImageUploadStyle>
          {images.map((image, index) => {
            return (
              <ImageStyle key={index}>
                <button
                  type="button"
                  onClick={() => onClickDeleteImageButton(index)}
                >
                  ×
                </button>
                <img src={image.url} alt="店舗画像" />
              </ImageStyle>
            );
          })}
        </ImageUploadStyle>
      )}
      <FilesField
        id="shop-image"
        placeholder="画像※10枚まで"
        mb="20px"
        files={images}
        setFiles={setImages}
        disabled={images.length >= 10}
      />
      <TextField
        type="text"
        id="shop-name"
        name="shop-name"
        label="店名(必須)"
        placeholder="店名(必須)"
        mb="20px"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required={true}
      />
      <TextField
        type="text"
        id="shop-name-kana"
        name="shop-name-kana"
        label="店名かな(必須)"
        placeholder="店名かな(必須)"
        mb="20px"
        value={nameKana}
        onChange={(event) => setNameKana(event.target.value)}
        required={true}
      />
      <TextField
        type="text"
        id="description"
        name="description"
        label="簡単な紹介"
        placeholder="簡単な紹介"
        mb="20px"
        value={description}
        onChange={(event) => setDescription(event.target.value)}
      />
      <TextField
        type="text"
        id="phone-number"
        name="phone-number"
        label="電話番号"
        placeholder="電話番号"
        mb="20px"
        value={tel}
        onChange={(event) => setTel(event.target.value)}
      />
      <TextField
        type="text"
        id="address"
        name="address"
        label="住所(必須)"
        placeholder="住所(必須)"
        mb="20px"
        value={address}
        onChange={(event) => setAddress(event.target.value)}
        required={true}
      />
      <TextField
        type="number"
        id="number-of-seats"
        name="number-of-seats"
        label="座席数"
        placeholder="座席数"
        mb="20px"
        onChange={(event) => setNumberOfSeats(event.target.valueAsNumber)}
      />
      <TextField
        type="text"
        id="nearest-station"
        name="nearest-station"
        label="最寄駅"
        placeholder="最寄駅"
        mb="25px"
        value={nearestStation}
        onChange={(event) => setNearestStation(event.target.value)}
      />
      <TextField
        type="text"
        id="homepage"
        name="homepage"
        label="ホームページ"
        placeholder="ホームページ"
        mb="20px"
        value={homepage}
        onChange={(event) => setHomepage(event.target.value)}
      />
      <TextField
        type="text"
        id="google-map-url"
        name="google-map-url"
        label="Google Map共有リンク"
        placeholder="Google Map共有リンク"
        mb="20px"
        value={googleMapUrl}
        onChange={(event) => setGoogleMapUrl(event.target.value)}
      />
      <TextField
        type="text"
        id="google-map-iframe"
        name="google-map-iframe"
        label="Google Map埋め込みコード"
        placeholder="Google Map埋め込みコード"
        mb="20px"
        value={googleMapIframe}
        onChange={(event) => setGoogleMapIframe(event.target.value)}
      />
      <FeaturesGroupStyle>
        <div className="title">特徴</div>
        {features.map((feature) => {
          return (
            <FeatureCheckboxField
              key={feature.id}
              id={"feature-" + feature.id.toString()}
              label={feature.name}
              mb="10px"
              onChange={() => onChangeFeatures(feature.id)}
            />
          );
        })}
      </FeaturesGroupStyle>
      <PaymentMethodsGroupStyle>
        <div className="title">お支払い方法</div>
        {paymentMethods.map((paymentMethod) => {
          return (
            <PaymentMethodCheckboxField
              key={paymentMethod.id}
              id={"payment-method-" + paymentMethod.id.toString()}
              label={paymentMethod.name}
              mb="10px"
              onChange={() => onChangeCheckedPaymentMethods(paymentMethod.id)}
            />
          );
        })}
      </PaymentMethodsGroupStyle>
      <BudgetGroupStyle>
        <div className="title">予算</div>
        <div className="timeframe-label">モーニング</div>
        <div className="timeframe-budget">
          <TextField
            type="number"
            placeholder="下限"
            onChange={(event) =>
              setMorningLowerBudget(event.target.valueAsNumber)
            }
          />
          <TextField
            type="number"
            placeholder="上限"
            onChange={(event) =>
              setMorningUpperBudget(event.target.valueAsNumber)
            }
          />
        </div>
        <div className="timeframe-label">ランチ</div>
        <div className="timeframe-budget">
          <TextField
            type="number"
            placeholder="下限"
            onChange={(event) =>
              setLunchLowerBudget(event.target.valueAsNumber)
            }
          />
          <TextField
            type="number"
            placeholder="上限"
            onChange={(event) =>
              setLunchUpperBudget(event.target.valueAsNumber)
            }
          />
        </div>
        <div className="timeframe-label">ディナー</div>
        <div className="timeframe-budget">
          <TextField
            type="number"
            placeholder="下限"
            onChange={(event) =>
              setEveningLowerBudget(event.target.valueAsNumber)
            }
          />
          <TextField
            type="number"
            placeholder="上限"
            onChange={(event) =>
              setEveningUpperBudget(event.target.valueAsNumber)
            }
          />
        </div>
      </BudgetGroupStyle>
      <OpeningHoursGroupStyle>
        <div className="title">営業時間</div>
        <OpeningHoursFieldStyle>
          <Select
            label="曜日"
            items={dayOfWeeks}
            selectItem={dayOfWeek}
            setSelectItemAction={setDayOfWeek}
          />
          <TextField
            placeholder="09:00"
            value={openingTime}
            onChange={(event) => setOpeningTime(event.target.value)}
          />
          <TextField
            placeholder="20:00"
            value={closingTime}
            onChange={(event) => setClosingTime(event.target.value)}
          />
          <OpeningHoursAddButtonStyle
            type="button"
            onClick={() => onClickAddOpeningHours()}
          >
            追加
          </OpeningHoursAddButtonStyle>
        </OpeningHoursFieldStyle>
        <OpeningHoursTable>
          <thead>
            <tr>
              <th>曜日</th>
              <th>開店時間</th>
              <th>閉店時間</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {openingHours &&
              openingHours.map((openingHour, index) => {
                return (
                  <tr key={index}>
                    <td>{dayOfWeekValues[openingHour["dayOfWeek"]]}</td>
                    <td>{openingHour["openingTime"]}</td>
                    <td>{openingHour["closingTime"]}</td>
                    <td
                      className="deleteOpeningHour"
                      onClick={() => onClickDeleteOpeningHour(index)}
                    >
                      <p>削除</p>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </OpeningHoursTable>
      </OpeningHoursGroupStyle>
      <TextAreaField
        id="note"
        name="note"
        label="備考"
        placeholder="備考"
        mb="20px"
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      <ButtonSubmitStyle
        value="送信"
        submitting={submitting}
        submittingMessage="送信中..."
      />
      {message && <Alert level={messageLevel}>{message}</Alert>}
    </CreateShopFormStyle>
  );
};
export default CreateDraftShopForm;
